import React, { useState } from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { IBranding } from './interfaces';
import { useStyles } from './styles';

interface ISelectBrandingModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (brandingId: string) => void;
  brandings: IBranding[];
}

const SelectBrandingModal = ({
  open,
  onClose,
  onSelect,
  brandings,
}: ISelectBrandingModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = () => {
    if (selectedId) {
      onSelect(selectedId);
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} sx={classes.modalRoot}>
      <Box sx={classes.selectContainer}>
        <Box sx={classes.selectHeader}>
          <Typography sx={classes.selectTitle}>
            {translate(
              'adminToolbox.selectBrandingTitle',
              'Select an existing branding as a base',
            )}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={classes.closeButton}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={classes.selectContent}>
          <Box sx={classes.selectList}>
            {brandings.map((branding) => (
              <Box
                key={branding.id}
                sx={[
                  classes.selectItem,
                  selectedId === branding.id && classes.selectItemActive,
                ]}
                onClick={() => setSelectedId(branding.id)}
              >
                <Typography sx={classes.selectItemName}>
                  {branding.name === 'Unnamed'
                    ? translate('adminToolbox.unnamed', 'Unnamed')
                    : branding.name}
                  {branding.isActive && (
                    <Typography component="span" sx={classes.currentLabel}>
                      {' '}
                      ({translate('adminToolbox.current', 'Current')})
                    </Typography>
                  )}
                </Typography>
                <Radio
                  checked={selectedId === branding.id}
                  sx={classes.radio}
                  size="small"
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={classes.selectFooter}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={classes.cancelButton}
          >
            {translate('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSelect}
            sx={classes.useSelectedButton}
            disabled={!selectedId}
          >
            {translate(
              'adminToolbox.useSelectedBranding',
              'Use selected branding',
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectBrandingModal;
